/**
 * ═══════════════════════════════════════════════════════════════════════════
 * MessageBubble.tsx - Componente de burbuja de mensaje individual
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * @description Componente para renderizar mensajes individuales en estilo burbuja
 * @version 1.0.1-pre.0.17
 * @architecture WASP Frontend
 * @optimization React.memo implementado para prevenir re-renders innecesarios
 * 
 * ═══════════════════════════════════════════════════════════════════════════
 */

import React, { memo } from 'react';
import { Badge } from 'react-bootstrap';

// Tipos
export interface Message {
  timestamp: string;
  sender: string;
  text: string;
  id?: string;
  type?: 'text' | 'image' | 'video' | 'audio' | 'document' | 'sticker';
  mediaUrl?: string;
}

interface MessageBubbleProps {
  message: Message;
  isBot?: boolean;
  showTimestamp?: boolean;
  compact?: boolean;
}

/**
 * Formatear timestamp a formato legible
 */
const formatTimestamp = (timestamp: string): string => {
  try {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    // Menos de 1 minuto
    if (diffMins < 1) {
      return 'Ahora';
    }

    // Menos de 1 hora
    if (diffMins < 60) {
      return `Hace ${diffMins} min`;
    }

    // Menos de 24 horas
    if (diffHours < 24) {
      return `Hace ${diffHours}h`;
    }

    // Menos de 7 días
    if (diffDays < 7) {
      return `Hace ${diffDays}d`;
    }

    // Fecha completa
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit'
    });
  } catch (error) {
    return timestamp;
  }
};

/**
 * Componente MessageBubble
 * 
 * Renderiza una burbuja de mensaje con estilo WhatsApp/iMessage
 * 
 * @optimization Memoizado con comparación personalizada para evitar re-renders
 * 
 * @example
 * ```tsx
 * <MessageBubble
 *   message={{
 *     timestamp: '2025-10-24T10:30:00',
 *     sender: 'bot',
 *     text: 'Hola! ¿En qué puedo ayudarte?'
 *   }}
 *   isBot={true}
 *   showTimestamp={true}
 * />
 * ```
 */
const MessageBubbleComponent: React.FC<MessageBubbleProps> = ({
  message,
  isBot = false,
  showTimestamp = true,
  compact = false
}) => {
  const isBotMessage = message.sender === 'user' || isBot;
  const isMyMessage = message.sender === 'bot';
  const isTheirMessage = message.sender === 'contact';
  const hasMedia = message.type && message.type !== 'text';


  return (
    <div
      className={`message-bubble-wrapper ${isMyMessage ? 'bot-message' : 'user-message'}`}
      style={{
        display: 'flex',
        justifyContent: isMyMessage ? 'flex-end' : 'flex-start',
        marginBottom: compact ? '8px' : '12px',
        animation: 'messageFadeIn 0.3s ease-out'
      }}
    >
      <div
        className="message-bubble"
        style={{
          maxWidth: '75%',
          padding: compact ? '8px 12px' : '10px 14px',
          borderRadius: '16px',
          backgroundColor: isMyMessage ? '#007bff' : '#f0f0f0',
          color: isMyMessage ? '#fff' : '#333',
          boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
          wordWrap: 'break-word',
          position: 'relative'
        }}
      >
        {/* Tipo de mensaje badge */}
        {hasMedia && (
          <Badge
            bg={isBotMessage ? 'secondary' : 'light'}
            text={isBotMessage ? 'light' : 'dark'}
            className="mb-2"
            style={{
              fontSize: '0.65rem',
              padding: '3px 6px'
            }}
          >
            {message.type?.toUpperCase()}
          </Badge>
        )}

        {/* Media preview */}
        {hasMedia && message.mediaUrl && (
          <div className="message-media mb-2">
            {message.type === 'image' && (
              <img
                src={message.mediaUrl}
                alt="Imagen"
                style={{
                  maxWidth: '100%',
                  borderRadius: '8px',
                  marginBottom: '8px'
                }}
                loading="lazy"
              />
            )}
            {message.type === 'video' && (
              <video
                src={message.mediaUrl}
                controls
                style={{
                  maxWidth: '100%',
                  borderRadius: '8px',
                  marginBottom: '8px'
                }}
              />
            )}
            {message.type === 'audio' && (
              <audio
                src={message.mediaUrl}
                controls
                style={{
                  width: '100%',
                  marginBottom: '8px'
                }}
              />
            )}
            {(message.type === 'document' || message.type === 'sticker') && (
              <div
                style={{
                  padding: '8px',
                  backgroundColor: 'rgba(0,0,0,0.05)',
                  borderRadius: '8px',
                  marginBottom: '8px',
                  fontSize: '0.9em'
                }}
              >
                📎 Archivo adjunto
              </div>
            )}
          </div>
        )}

        {/* Texto del mensaje */}
        {message.text && (
          <div
            className="message-text"
            style={{
              fontSize: compact ? '0.9rem' : '0.95rem',
              lineHeight: '1.4',
              whiteSpace: 'pre-wrap'
            }}
          >
            {message.text}
          </div>
        )}

        {/* Timestamp */}
        {showTimestamp && (
          <div
            className="message-timestamp"
            style={{
              fontSize: '0.7rem',
              marginTop: '4px',
              opacity: 0.7,
              textAlign: 'right'
            }}
          >
            {formatTimestamp(message.timestamp)}
          </div>
        )}

        {/* Triangle pointer */}
        <div
          style={{
            position: 'absolute',
            bottom: '8px',
            [isMyMessage ? 'right' : 'left']: '-6px', // ← CAMBIO
            width: 0,
            height: 0,
            borderStyle: 'solid',
            borderWidth: isMyMessage // ← CAMBIO
              ? '0 0 8px 8px'      // Mis mensajes: triángulo derecha
              : '0 8px 8px 0',     // Recibidos: triángulo izquierda
            borderColor: isMyMessage // ← CAMBIO
              ? 'transparent transparent transparent #007bff'
              : 'transparent #f0f0f0 transparent transparent'
            }}
        />
      </div>

      <style>{`
        @keyframes messageFadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .message-bubble {
          transition: all 0.2s ease;
        }

        .message-bubble:hover {
          box-shadow: 0 2px 8px rgba(0,0,0,0.15);
        }

        /* Responsive */
        @media (max-width: 768px) {
          .message-bubble {
            max-width: 85% !important;
            font-size: 0.9rem !important;
          }
        }

        /* Dark mode ready */
        @media (prefers-color-scheme: dark) {
          .bot-message .message-bubble {
            background-color: #2a2a2a;
            color: #e0e0e0;
          }
        }
      `}</style>
    </div>
  );
};

/**
 * Función de comparación personalizada para React.memo
 * Solo re-renderiza si cambian propiedades relevantes del mensaje
 */
const arePropsEqual = (
  prevProps: MessageBubbleProps,
  nextProps: MessageBubbleProps
): boolean => {
  // Comparar propiedades simples
  if (
    prevProps.isBot !== nextProps.isBot ||
    prevProps.showTimestamp !== nextProps.showTimestamp ||
    prevProps.compact !== nextProps.compact
  ) {
    return false;
  }

  // Comparación profunda del mensaje
  const prevMsg = prevProps.message;
  const nextMsg = nextProps.message;

  return (
    prevMsg.id === nextMsg.id &&
    prevMsg.timestamp === nextMsg.timestamp &&
    prevMsg.sender === nextMsg.sender &&
    prevMsg.text === nextMsg.text &&
    prevMsg.type === nextMsg.type &&
    prevMsg.mediaUrl === nextMsg.mediaUrl
  );
};

/**
 * Exportar componente memoizado
 * Previene re-renders cuando las props no cambian
 */
export const MessageBubble = memo(MessageBubbleComponent, arePropsEqual);

export default MessageBubble;